import { BaiHoc } from '../../types'
import { SimulationService } from '../simulation/SimulationService'

interface LessonData {
  id: number
  tieuDe: string
  moTa: string
  metadata: {
    thoiGian: number
    doKho: string
    chuDe: string[]
    mucTieu: string[]
  }
  noiDung: {
    phanMoDau: {
      tieuDe: string
      noiDung: any[]
    }
    phanNoiDung: {
      tieuDe: string
      noiDung: any[]
    }
    phanCuoi: {
      tieuDe: string
      noiDung: any[]
    }
  }
  congThuc?: any[]
}

export class ContentServiceSimple {
  private static contentCache = new Map<string, BaiHoc>()
  private static rawLessonCache = new Map<string, LessonData>()
  private static isInitialized = false
  private static initPromise: Promise<void> | null = null

  static async initialize(): Promise<void> {
    if (this.isInitialized) return
    if (this.initPromise) return this.initPromise

    this.initPromise = (async () => {
      try {
        console.log('📚 Starting ContentServiceSimple initialization...')
        const startTime = performance.now()

        // Use Vite's import.meta.glob to find all noi-dung.json files
        // Eager load (eager: true) to get data immediately
        const modules = import.meta.glob('../../du-lieu/noi-dung/lop-*/chuong-*/*/noi-dung.json', {
          eager: true,
          import: 'default'
        })

        const paths = Object.keys(modules);
        console.log(`📚 ContentServiceSimple: Found ${paths.length} potential lesson files.`);
        if (paths.length > 0) {
          console.log(`📚 First 3 paths:`, paths.slice(0, 3));
        } else {
          console.warn('⚠️ ContentServiceSimple: NO lesson files found matching glob pattern!');
        }

        let count = 0
        for (const path in modules) {
          try {
            const lessonData = modules[path] as LessonData

            // Extract info from path
            // Path format: ../../du-lieu/noi-dung/lop-10/chuong-1/bai-1-gioi-thieu/noi-dung.json
            // Regex updated to handle folder suffixes (e.g., chuong-1-dong-hoc)
            // Updated Regex to be more permissive with optional separators
            const match = path.match(/lop-(\d+)[^/]*\/chuong-(\d+)[^/]*\/bai-(\d+)/)

            if (match && lessonData) {
              const [_, lopStr, chuongStr, baiStr] = match
              const lop = parseInt(lopStr)
              const chuong = parseInt(chuongStr)
              const bai = parseInt(baiStr)

              const cacheKey = `lop-${lop}-chuong-${chuong}-bai-${bai}`

              // Store raw data
              this.rawLessonCache.set(cacheKey, lessonData)
              count++
              // Log first few successful loads to verify regex
              if (count <= 3) {
                console.log(`✅ Loaded lesson: ${cacheKey} from ${path}`);
              }
            } else {
              console.warn(`⚠️ Skipped path (regex mismatch or no data): ${path}`);
            }
          } catch (err) {
            console.warn(`Failed to parse lesson at ${path}`, err)
          }
        }

        this.isInitialized = true
        const endTime = performance.now()
        console.log(`✅ ContentServiceSimple initialized: ${count} lessons in ${(endTime - startTime).toFixed(2)}ms`)
      } catch (error) {
        console.error('❌ Failed to initialize ContentServiceSimple:', error)
        throw error
      } finally {
        this.initPromise = null
      }
    })()

    return this.initPromise
  }

  static async loadLesson(lop: number, chuong: number, bai: number): Promise<BaiHoc | null> {
    // Ensure initialized
    if (!this.isInitialized) {
      await this.initialize()
    }

    const lessonId = `lop-${lop}-chuong-${chuong}-bai-${bai}`

    console.log(`🔍 Loading lesson: ${lessonId}`)

    // Check processed cache first
    if (this.contentCache.has(lessonId)) {
      console.log(`📦 Returning cached lesson: ${lessonId}`)
      return this.contentCache.get(lessonId)!
    }

    try {
      // Get raw data from cache
      const lessonData = this.rawLessonCache.get(lessonId)

      if (lessonData) {
        console.log(`✅ Found raw lesson data for: ${lessonId}`)

        // Load simulations for this lesson
        const simulations = await this.loadLessonSimulations(lop, chuong, bai)

        // Inject simulations into lesson data
        const enrichedLessonData = this.injectSimulationsIntoLesson(lessonData, simulations)

        const convertedLesson = this.convertLessonData(enrichedLessonData, lop, chuong, bai)

        // Cache processed lesson
        this.contentCache.set(lessonId, convertedLesson)

        return convertedLesson
      }

      console.log(`❌ No lesson data found for ${lessonId}`)
      return null
    } catch (error) {
      console.error(`💥 Failed to load lesson ${lessonId}:`, error)
      return null
    }
  }

  /**
   * Load simulations for a lesson
   */
  private static async loadLessonSimulations(lop: number, chuong: number, bai: number): Promise<any[]> {
    try {
      // console.log(`🎮 Loading simulations for lop ${lop}, chuong ${chuong}, bai ${bai}`)
      const simulations = await SimulationService.loadAllLessonSimulations(lop, chuong, bai)
      // console.log(`✅ Loaded ${simulations.length} simulations`)
      return simulations
    } catch (error) {
      console.warn(`⚠️ No simulations found for lop ${lop}, chuong ${chuong}, bai ${bai}:`, error)
      return []
    }
  }

  /**
   * Inject simulations into lesson data based on their section
   */
  private static injectSimulationsIntoLesson(lessonData: LessonData, simulations: any[]): LessonData {
    // console.log(`🔄 Preparing to inject ${simulations.length} simulations into lesson`)

    // Deep clone to ensure we don't mutate original data
    const enrichedLessonData = JSON.parse(JSON.stringify(lessonData))

    if (simulations.length === 0) {
      return enrichedLessonData
    }

    // Create a map of simulations by ID for quick lookup
    const simMap = new Map()
    simulations.forEach(sim => {
      if (sim.id && sim.data) {
        simMap.set(sim.id, sim.data)
      }
    })

    // Replace simulation_ref items with actual simulation data
    const replaceSimulationRefs = (items: any[], sectionName: string) => {
      if (!items || !Array.isArray(items)) {
        return []
      }

      const processed = items.map(item => {
        if (item && item.type === 'simulation_ref') {
          const simId = item.simId || item.simulation_id
          if (simId && simMap.has(simId)) {
            const simData = simMap.get(simId)
            // console.log(`  🔄 ${sectionName}: Replaced simulation_ref '${simId}' with actual data`)
            return simData
          } else {
            console.warn(`  ⚠️ ${sectionName}: simulation_ref '${simId}' not found in simMap`)
            return item
          }
        }
        return item
      })

      return processed
    }

    // Apply replacement to each section
    enrichedLessonData.noiDung.phanMoDau.noiDung = replaceSimulationRefs(
      enrichedLessonData.noiDung.phanMoDau.noiDung,
      'phanMoDau'
    )
    enrichedLessonData.noiDung.phanNoiDung.noiDung = replaceSimulationRefs(
      enrichedLessonData.noiDung.phanNoiDung.noiDung,
      'phanNoiDung'
    )
    if (enrichedLessonData.noiDung.phanCuoi) {
      enrichedLessonData.noiDung.phanCuoi.noiDung = replaceSimulationRefs(
        enrichedLessonData.noiDung.phanCuoi.noiDung,
        'phanCuoi'
      )
    }

    return enrichedLessonData
  }

  private static convertLessonData(lessonData: LessonData, lop: number, chuong: number, bai: number): BaiHoc {
    // console.log('🔄 Converting lesson data:', lessonData.tieuDe)

    const convertedLesson = {
      id: `${lop}-${chuong}-${bai}`,
      tieu_de: lessonData.tieuDe || 'Không có tiêu đề',
      lop: lop,
      chuong: chuong,
      so_bai: bai,
      thoi_luong: lessonData.metadata?.thoiGian || 45,
      muc_tieu: lessonData.metadata?.mucTieu || [],
      cac_phan: [
        {
          id: "mo_dau",
          tieu_de: lessonData.noiDung?.phanMoDau?.tieuDe || 'Phần mở đầu',
          noi_dung: this.convertContentItems(lessonData.noiDung?.phanMoDau?.noiDung || []),
          thoi_gian_uoc_tinh: Math.ceil((lessonData.metadata?.thoiGian || 45) * 0.3)
        },
        {
          id: "noi_dung",
          tieu_de: lessonData.noiDung?.phanNoiDung?.tieuDe || 'Phần nội dung',
          noi_dung: this.convertContentItems(lessonData.noiDung?.phanNoiDung?.noiDung || []),
          thoi_gian_uoc_tinh: Math.ceil((lessonData.metadata?.thoiGian || 45) * 0.5)
        },
        {
          id: "cuoi",
          tieu_de: lessonData.noiDung?.phanCuoi?.tieuDe || 'Phần cuối',
          noi_dung: this.convertContentItems(lessonData.noiDung?.phanCuoi?.noiDung || []),
          thoi_gian_uoc_tinh: Math.ceil((lessonData.metadata?.thoiGian || 45) * 0.2)
        }
      ]
    }

    return convertedLesson
  }

  private static convertContentItems(contentItems: any[]): any[] {
    if (!Array.isArray(contentItems)) {
      return []
    }

    return contentItems.map(item => {
      if (!item || typeof item !== 'object') {
        return { type: 'text', content: 'Invalid content item' }
      }

      switch (item.type) {
        case 'text':
          return {
            type: 'text',
            content: item.content || ''
          }
        case 'heading':
          return {
            type: 'heading',
            level: item.level || 1,
            content: item.content || ''
          }
        case 'formula':
          return {
            type: 'formula',
            content: item.content || '',
            explanation: item.explanation || '',
            display: item.display || 'inline'
          }
        case 'section':
          // Handle section type with nested content
          return {
            type: 'section',
            title: item.title || '',
            content: Array.isArray(item.content) ? this.convertContentItems(item.content) : []
          }
        case 'list':
          return {
            type: 'list',
            style: item.style || 'bullet',
            items: Array.isArray(item.items) ? item.items : []
          }
        case 'image':
          return {
            type: 'image',
            url: item.url || item.src || '',
            src: item.src || item.url || '',
            content: item.content || '',
            caption: item.caption || '',
            alt: item.alt || '',
            width: item.width,
            height: item.height
          }
        case 'video':
          return {
            type: 'video',
            url: item.url || item.src || '',
            src: item.src || item.url || '',
            content: item.content || '',
            caption: item.caption || '',
            title: item.title || '',
            description: item.description || '',
            provider: item.provider || 'youtube'
          }
        case 'quiz':
          return {
            type: 'quiz',
            id: item.id || '',
            question: item.question || '',
            options: Array.isArray(item.options) ? item.options : [],
            answer: item.answer || ''
          }
        case 'callout':
          return {
            type: 'callout',
            style: item.style || 'info',
            title: item.title || '',
            content: item.content || ''
          }
        default:
          return item
      }
    })
  }

  static clearCache() {
    this.contentCache.clear()
    this.rawLessonCache.clear()
    this.isInitialized = false
    this.initPromise = null
  }
}